import clsx from 'clsx';
import { forwardRef } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';

/**
 * Primitivas do shell ADMINISTRATIVO (desktop-first).
 *
 * Portado do design system do Adonai, mas o contexto aqui é outro: escritório
 * de contabilidade, uso sentado, de leitura longa de tabela — sem o alvo
 * grande nem o feedback sonoro do chão de fábrica do Adonai, que não se
 * aplica neste produto.
 *
 * Regra que se mantém: nenhuma cor escrita na mão. Toda cor sai de token
 * semântico do `@theme`, definido em `estilos.css`.
 */

type Variante = 'primaria' | 'secundaria' | 'perigo' | 'fantasma';
type Tamanho = 'padrao' | 'icone';

// Variante é SÓ cor de repouso + hover. Desabilitado NUNCA troca cor: é um
// único comportamento global na base (opacidade). Assim qualquer variante nova
// já nasce certa nos dois temas.
// O desabilitado tem de continuar sendo a MARCA a 50%, não um cinza fixo:
// `disabled:bg-tinta-300` pintava um bloco claro com rótulo quase preto no
// escuro (2.05:1) e rótulo branco sobre cinza no claro (~2.0:1) — o "botão
// claro" que o dono viu.
// `enabled:hover:` em vez do `disabled:pointer-events-none` do isotec: o
// `pointer-events-none` mataria o `title` que explica por que o botão está
// morto e anularia o `disabled:cursor-not-allowed`. Como o `disabled:bg-*`
// saiu, é o `enabled:` que impede o hover de repintar o botão desabilitado.
const VARIANTES: Record<Variante, string> = {
  primaria: 'bg-marca-600 text-marca-contraste enabled:hover:bg-marca-500',
  // `bg-fundo`, não `bg-superficie`: o painel do PaginaShell JÁ é `superficie`,
  // então o preenchimento sumia e só o contorno aparecia. O botão afunda um
  // degrau, como o `outline` do isotec (`bg-background` sobre `card`).
  // `border`, não `ring`: `ring` é box-shadow FORA da caixa — soma 2px de
  // pegada e nunca alinha com o `border` do CAMPO_BASE ao lado.
  // `border-tinta-400`, não `tinta-100`: é borda de botão de verdade, e
  // `tinta-300` só dá 2.00:1 claro / 1.95:1 escuro (SC 1.4.11 exige 3.0:1).
  // Borda de hover cheia, não `/40` como o isotec: cheia mede 4.75:1 claro /
  // 4.02:1 escuro; `/40` cai para 1.76/1.54 e derrubaria o `tinta-400`.
  secundaria:
    'bg-fundo text-tinta-900 border border-tinta-100 enabled:hover:border-marca-600 enabled:hover:bg-marca-600/10',
  perigo: 'bg-erro-600 text-marca-contraste enabled:hover:opacity-90',
  fantasma: 'text-tinta-700 enabled:hover:bg-marca-600/10',
};

// A altura mora AQUI, no componente — é isso que faz a próxima tela nascer
// certa. `h-11` (41,25px) é o size default do `button.tsx` do isotec e a mesma
// altura do input dele; cada tela remendando com `h-10` era o que deixava a
// barra 10% mais baixa e fazia a tela seguinte errar de novo.
// `icone` existe porque este arquivo usa `clsx`, não `tailwind-merge`: sem um
// tamanho próprio, o botão só-ícone herda o `px-4` do padrão e sai oblongo.
const TAMANHOS: Record<Tamanho, string> = {
  padrao: 'h-11 px-4 py-2',
  icone: 'h-11 w-11',
};

/**
 * A aparência da AÇÃO PRIMÁRIA, para quem não pode ser um `<button>`.
 *
 * O caso real: "Nova pessoa" e "Nova sociedade" NAVEGAM, então são `<a>`. Um
 * `<Botao>` envolvendo um `<Link>` produz `<button>` dentro de `<a>` — HTML
 * inválido, e o teclado passa a ver dois controles onde a pessoa vê um.
 *
 * Existe como constante, e não copiada em cada tela, pelo motivo que a skill
 * `social-desenho` registra: string repetida é como o padrão se perde. E há um
 * motivo a mais aqui — o portão de contraste varre as classes REAIS do código
 * e pareia `bg-*` com `hover:bg-*` do mesmo elemento; uma cópia solta vira um
 * par novo para o portão medir, e reprova sem que nada tenha mudado de fato.
 *
 * Sem `enabled:`: link não tem estado desabilitado, e o prefixo ali só
 * confundiria quem lesse depois.
 *
 * E SEM anel de foco próprio: o `estilos.css` já tem `:focus-visible` global,
 * medido pelo portão contra as três superfícies. Declarar um `ring` local
 * aqui duplicava o anel e criava um par novo — anel sobre o fundo do próprio
 * botão —, que o portão reprovava com razão aparente. O `Botao` nunca teve
 * anel próprio; este link também não tem.
 */
export const ACAO_PRIMARIA_LINK =
  'bg-marca-600 text-marca-contraste hover:bg-marca-500 ' +
  'inline-flex h-11 items-center gap-2 rounded-lg px-4 text-sm font-medium ' +
  'whitespace-nowrap transition duration-150 hover:scale-[1.02] active:scale-[0.97]';

interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  tamanho?: Tamanho;
  carregando?: boolean;
}

export function Botao({
  variante = 'primaria',
  tamanho = 'padrao',
  carregando = false,
  className,
  children,
  disabled,
  ...resto
}: BotaoProps) {
  return (
    <button
      type="button"
      // Desabilitado só enquanto envia: bloquear por validação esconde do
      // usuário o que está faltando.
      disabled={disabled === true || carregando}
      aria-busy={carregando}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg',
        TAMANHOS[tamanho],
        'text-sm font-medium transition duration-150',
        // O botão não dava retorno nenhum de clique; estes são os valores do
        // `button.tsx` do isotec.
        'hover:scale-[1.02] active:scale-[0.97]',
        // `disabled:hover:scale-100` NÃO é redundância aqui: como não migramos
        // o `pointer-events-none` do isotec (ele mataria o `title` do botão
        // desabilitado), é esta regra que trava a escala no botão morto.
        'disabled:hover:scale-100 disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTES[variante],
        className,
      )}
      {...resto}
    >
      {carregando && (
        <span
          aria-hidden
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

interface CampoProps {
  rotulo: string;
  erro?: string | undefined;
  dica?: string | undefined;
  obrigatorio?: boolean;
  children: ReactNode;
  id: string;
}

export function Campo({ rotulo, erro, dica, obrigatorio, children, id }: CampoProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-tinta-700">
        {rotulo}
        {obrigatorio && (
          <span className="text-erro-600 ml-0.5" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {/* O erro fica embaixo do campo, em português, sem jargão. */}
      {erro ? (
        <p id={`${id}-erro`} role="alert" className="text-erro-600 text-sm">
          {erro}
        </p>
      ) : (
        dica && <p className="text-tinta-500 text-xs">{dica}</p>
      )}
    </div>
  );
}

// `border-tinta-100`, a MESMA borda dos cards: decisão do dono, para o
// contorno não puxar para o claro no tema escuro nem para o escuro no tema
// claro. O que se troca está medido e registrado em `scripts/contraste.mjs`
// (busque CONTORNO_SUAVE): sai de 3.06:1 para 1.13:1 claro / 1.28:1 escuro, e
// deixa de atender SC 1.4.11. Quem identifica o controle passa a ser o fundo
// (`bg-campo`) mais o texto, não a borda.
// Placeholder em `tinta-500`, não `tinta-300`: texto exige 4.5:1 (SC 1.4.3),
// e `tinta-300` dava o mesmo 2.00:1/1.95:1 que a borda; `tinta-500` mede
// 4.84:1 claro / 5.29:1 escuro contra `--color-superficie`.
// `h-11` mora aqui pelo mesmo motivo de `TAMANHOS`: é a altura do input do
// isotec, e casa campo e botão na mesma barra sem a tela precisar dizer nada.
/**
 * A aparência de TODO controle da barra de ferramentas.
 *
 * É constante, e não uma string repetida por componente, porque foi assim que
 * a barra saiu do padrão: cada controle nasceu com a sua própria combinação,
 * uns com `bg-campo` e outros só com borda, e o resultado foi o dono apontando
 * que os botões não pareciam da mesma família. Um lugar só para mudar.
 *
 * `bg-campo` é o ponto: só borda sobre a laje deixa o controle rente ao fundo
 * e a barra some visualmente. O controle afunda um degrau, como o campo de
 * formulário ao lado dele — é a mesma camada, e a mesma leitura.
 *
 * Só a moldura mora aqui. Quem usa acrescenta o espaçamento conforme a forma:
 * `px-3` para controle com texto, `w-11 justify-center` para botão-ícone.
 */
export const CONTROLE_BARRA =
  'border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 ' +
  'inline-flex h-11 items-center gap-2 rounded-lg border text-sm whitespace-nowrap transition-colors';

const CAMPO_BASE =
  'h-11 w-full rounded-lg border border-tinta-100 bg-campo px-3 py-2 text-sm ' +
  'placeholder:text-tinta-500 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-erro-600';

/*
  `forwardRef` nos dois, e não é detalhe de estilo.

  O `react-hook-form` registra o campo pela REF: `{...register('nome')}`
  devolve `name`, `onChange`, `onBlur` e `ref`, e é a `ref` que dá a ele o
  elemento para ler valor, focar no erro e resetar. Um componente que engole a
  `ref` produz um formulário que PARECE funcionar — os campos digitam, o
  `onChange` dispara — mas o `watch` nunca vê o valor e o `handleSubmit` envia
  vazio.

  Foi assim que apareceu: um campo condicional (regime de bens, que só existe
  para quem é casado) não aparecia ao escolher CASADO, porque o `watch` do
  estado civil não enxergava nada.
*/
export const Entrada = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { erro?: boolean }
>(function Entrada({ className, erro, ...resto }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={erro === true}
      aria-describedby={erro === true ? `${resto.id}-erro` : undefined}
      className={clsx(CAMPO_BASE, className)}
      {...resto}
    />
  );
});

export const Selecao = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { erro?: boolean }
>(function Selecao({ className, erro, children, ...resto }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={erro === true}
      className={clsx(CAMPO_BASE, className)}
      {...resto}
    >
      {children}
    </select>
  );
});

export function Etiqueta({
  tom = 'neutro',
  children,
}: {
  tom?: 'neutro' | 'ok' | 'alerta' | 'erro';
  children: ReactNode;
}) {
  const tons = {
    neutro: 'bg-tinta-100 text-tinta-700',
    ok: 'bg-ok-100 text-ok-600',
    alerta: 'bg-alerta-100 text-alerta-600',
    erro: 'bg-erro-100 text-erro-600',
  };
  return (
    <span
      className={clsx(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
        tons[tom],
      )}
    >
      {children}
    </span>
  );
}

/** Aviso de erro no topo de um formulário ou de uma tela. */
export function Aviso({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm">
      {children}
    </div>
  );
}
